if (!customElements.get('media-gallery')) {
  customElements.define(
    'media-gallery',
    class MediaGallery extends HTMLElement {
      constructor() {
        super();
        this.elements = {
          liveRegion: this.querySelector('[id^="GalleryStatus"]'),
          viewer: this.querySelector('[id^="GalleryViewer"]'),
          thumbnails: this.querySelector('[id^="GalleryThumbnails"]'),
        };
        this.mql = window.matchMedia('(min-width: 750px)');
        if (!this.elements.thumbnails) return;

        this.elements.viewer.addEventListener('slideChanged', debounce(this.onSlideChanged.bind(this), 500));
        this.elements.thumbnails.querySelectorAll('[data-target]').forEach((mediaToSwitch) => {
          mediaToSwitch
            .querySelector('button')
            .addEventListener('click', this.setActiveMedia.bind(this, mediaToSwitch.dataset.target, false));
        });
        if (this.dataset.desktopLayout.includes('thumbnail') && this.mql.matches) this.removeListSemantic();
      }

      connectedCallback() {
        // On load with ?variant= in URL: scroll viewer and thumbnails to the active slide (natural order, no prepend)
        const runScrollToActive = () => {
          const activeMedia = this.elements.viewer?.querySelector('.product__media-item.is-active')
            || this.elements.viewer?.querySelector('[data-media-id]');
          if (!activeMedia?.dataset?.mediaId || !this.elements.thumbnails) return;
          const slider = activeMedia.parentElement;
          if (slider && typeof slider.scrollTo === 'function') {
            slider.scrollTo({ left: activeMedia.offsetLeft, behavior: 'auto' });
          }
          const activeThumbnail = this.elements.thumbnails.querySelector(
            `[data-target="${activeMedia.dataset.mediaId}"]`
          );
          if (activeThumbnail) this.setActiveThumbnail(activeThumbnail);
        };
        requestAnimationFrame(() => {
          requestAnimationFrame(() => setTimeout(runScrollToActive, 0));
        });
      }

      onSlideChanged(event) {
        const now = Date.now();
        const mediaId = event.detail?.currentElement?.dataset?.mediaId;
        console.log('[media-gallery] onSlideChanged', {
          mediaId,
          currentPage: event.detail?.currentPage,
          now,
          _ignoreSlideChangedUntil: this._ignoreSlideChangedUntil,
          willIgnore: this._ignoreSlideChangedUntil && now < this._ignoreSlideChangedUntil,
        });
        // Ignore slideChanged for a short period after programmatic setActiveMedia (e.g. variant change)
        // so the debounced event from the viewer scroll doesn't reset the thumbnail back to the first slide
        if (this._ignoreSlideChangedUntil && now < this._ignoreSlideChangedUntil) {
          console.log('[media-gallery] onSlideChanged IGNORED (within ignore window)');
          return;
        }
        const thumbnail = this.findThumbnailByMediaId(mediaId);
        console.log('[media-gallery] onSlideChanged setting thumbnail', mediaId, !!thumbnail);
        this.setActiveThumbnail(thumbnail);
      }

      findThumbnailByMediaId(mediaId) {
        if (!this.elements.thumbnails || !mediaId) return null;

        const exactMatch = this.elements.thumbnails.querySelector(`[data-target="${mediaId}"]`);
        if (exactMatch) return exactMatch;

        const mediaSuffix = mediaId.split('-').pop();
        if (!mediaSuffix) return null;
        return this.elements.thumbnails.querySelector(`[data-target$="-${mediaSuffix}"]`);
      }

      setActiveMedia(mediaId, prepend, retries = 8) {
        console.log('[media-gallery] setActiveMedia called', { mediaId, prepend });
        let activeMedia = this.elements.viewer.querySelector(`[data-media-id="${mediaId}"]`);
        if (!activeMedia && mediaId) {
          const mediaSuffix = mediaId.split('-').pop();
          if (mediaSuffix) {
            activeMedia = this.elements.viewer.querySelector(`[data-media-id$="-${mediaSuffix}"]`);
          }
        }
        if (!activeMedia) {
          if (retries > 0) {
            requestAnimationFrame(() => this.setActiveMedia(mediaId, prepend, retries - 1));
            return;
          }
          console.log('[media-gallery] setActiveMedia no activeMedia found after retries');
          return;
        }
        this.elements.viewer.querySelectorAll('[data-media-id]').forEach((element) => {
          element.classList.remove('is-active');
        });
        activeMedia?.classList?.add('is-active');

        if (prepend) {
          activeMedia.parentElement.firstChild !== activeMedia && activeMedia.parentElement.prepend(activeMedia);

          if (this.elements.thumbnails) {
            const activeThumbnail = this.elements.thumbnails.querySelector(`[data-target="${mediaId}"]`);
            activeThumbnail.parentElement.firstChild !== activeThumbnail && activeThumbnail.parentElement.prepend(activeThumbnail);
          }

          if (this.elements.viewer.slider) this.elements.viewer.resetPages();
        }

        this.preventStickyHeader();
        // Ignore slideChanged for 2.5s so late ResizeObserver (after updateMedia reorder) + 500ms debounce don't reset thumbnail
        this._ignoreSlideChangedUntil = Date.now() + 2500;
        console.log('[media-gallery] setActiveMedia set _ignoreSlideChangedUntil', this._ignoreSlideChangedUntil, '(now + 2500ms)');
        window.setTimeout(() => {
          // Always scroll when thumbnails are clicked (thumbnail click scenario)
          if (this.elements.thumbnails || !this.mql.matches) {
            const scrollableElement = activeMedia.parentElement;
            // Ensure smooth scrolling behavior
            scrollableElement.scrollTo({ left: activeMedia.offsetLeft, behavior: 'smooth' });
          }
          const activeMediaRect = activeMedia.getBoundingClientRect();
          // Don't scroll if the image is already in view
          if (activeMediaRect.top > -0.5) return;
          const top = activeMediaRect.top + window.scrollY;
          window.scrollTo({ top: top, behavior: 'smooth' });
        });
        this.playActiveMedia(activeMedia);

        if (!this.elements.thumbnails) return;
        const activeThumbnail = this.findThumbnailByMediaId(activeMedia.dataset.mediaId || mediaId);
        this.setActiveThumbnail(activeThumbnail);
        if (activeThumbnail) {
          this.announceLiveRegion(activeMedia, activeThumbnail.dataset.mediaPosition);
        }
      }

      setActiveThumbnail(thumbnail) {
        if (!this.elements.thumbnails || !thumbnail) return;

        this.elements.thumbnails
          .querySelectorAll('button')
          .forEach((element) => element.removeAttribute('aria-current'));
        thumbnail.querySelector('button').setAttribute('aria-current', true);
        
        // Always scroll to position the thumbnail at the first position
        // Use setTimeout to ensure DOM updates complete first
        setTimeout(() => {
          const slider = this.elements.thumbnails.slider;
          if (!slider) return;
          
          const sliderRect = slider.getBoundingClientRect();
          const thumbnailRect = thumbnail.getBoundingClientRect();
          
          // Check if this is a vertical slider
          // On mobile (< 750px), the slider is always horizontal regardless of the class
          const isDesktop = window.innerWidth >= 750;
          const hasVerticalClass = this.elements.thumbnails.classList.contains('vertical-media-gallery');
          const isVertical = isDesktop && hasVerticalClass;
          
          if (isVertical) {
            // For vertical sliders, scroll to position thumbnail at top
            // Subtract 1px to compensate for the active border
            const scrollOffset = thumbnailRect.top - sliderRect.top + slider.scrollTop - 1;
            slider.scrollTo({ top: scrollOffset, behavior: 'smooth' });
          } else {
            // For horizontal sliders, scroll to position thumbnail at left
            // Subtract 1px to compensate for the active border
            const scrollOffset = thumbnailRect.left - sliderRect.left + slider.scrollLeft - 1;
            slider.scrollTo({ left: scrollOffset, behavior: 'smooth' });
          }
        }, 50);
      }

      announceLiveRegion(activeItem, position) {
        const image = activeItem.querySelector('.product__modal-opener--image img');
        if (!image) return;
        image.onload = () => {
          this.elements.liveRegion.setAttribute('aria-hidden', false);
          this.elements.liveRegion.innerHTML = window.accessibilityStrings.imageAvailable.replace('[index]', position);
          setTimeout(() => {
            this.elements.liveRegion.setAttribute('aria-hidden', true);
          }, 2000);
        };
        image.src = image.src;
      }

      playActiveMedia(activeItem) {
        window.pauseAllMedia();
        const deferredMedia = activeItem.querySelector('.deferred-media');
        if (deferredMedia) deferredMedia.loadContent(false);
      }

      preventStickyHeader() {
        this.stickyHeader = this.stickyHeader || document.querySelector('sticky-header');
        if (!this.stickyHeader) return;
        this.stickyHeader.dispatchEvent(new Event('preventHeaderReveal'));
      }

      removeListSemantic() {
        if (!this.elements.viewer.slider) return;
        this.elements.viewer.slider.setAttribute('role', 'presentation');
        this.elements.viewer.sliderItems.forEach((slide) => slide.setAttribute('role', 'presentation'));
      }
    }
  );
}
